import { EditableOverrideReasonOption } from '../../../../shared/editable-review-table/editable-review-table.model';
import { UploadDocumentType } from './upload-documents-preview.model';
import {
  DOCUMENT_TYPE_ALIASES_BASE,
  EXTRACTION_COMMON_KEY_ALIASES_BASE,
  EXTRACTION_DOC_TYPE_KEY_ALIASES_BASE,
} from '../../../../core/constants/document-ocr.constants';

export const PRIMARY_DOCUMENT_TYPES_BASE: UploadDocumentType[] = [
  'Passport',
  'ResidenceVisa',
  'EmiratesId',
  'ParentInsurance',
  'DischargeSummary',
  'BirthCertificate',
  'UndertakingLetter',
];

export const POLICY_DOCUMENT_TYPES: UploadDocumentType[] = ['LabourContract'];

export const ADDITIONAL_DOCUMENT_TYPES: UploadDocumentType[] = [
  'KYC',
  'Census',
  'FitnessForm',
  'Others',
];

export const SPONSOR_DOCUMENT_TYPES: UploadDocumentType[] = [
  'TradeLicense',
  'EstablishmentCard',
  'DirectorsIDs',
  'MOA_AOA_HolderIDs',
  'VATCertificate',
];

export const OPTIONAL_SPONSOR_DOCUMENT_TYPES: UploadDocumentType[] = ['VATCertificate'];

export const EXTRACTION_SUPPORTED_DOCUMENT_TYPES: UploadDocumentType[] = [
  'Passport',
  'ResidenceVisa',
  'EmiratesId',
  'EstablishmentCard',
  'VATCertificate',
];

export const MINOR_ONLY_DOCUMENT_TYPES: UploadDocumentType[] = [
  'ParentInsurance',
  'DischargeSummary',
  'BirthCertificate',
];

export const MANDATORY_BASE_DOCUMENT_TYPES: UploadDocumentType[] = [
  'Passport',
  'ResidenceVisa',
  'EmiratesId',
];

export const MANDATORY_SPONSOR_DOCUMENT_TYPES: UploadDocumentType[] = [
  'TradeLicense',
  'EstablishmentCard',
  'DirectorsIDs',
  'MOA_AOA_HolderIDs',
];

export const DOCUMENT_TYPE_ALIASES: Record<string, UploadDocumentType> =
  DOCUMENT_TYPE_ALIASES_BASE as Record<string, UploadDocumentType>;

export const DOCUMENT_LABELS: Record<string, string> = {
  Passport: 'Passport',
  ResidenceVisa: 'Visa / Entry Permit',
  EmiratesId: 'Emirates ID / Emirates ID Application',
  ParentInsurance: 'Parent Insurance Proof',
  DischargeSummary: 'Discharge Summary Card',
  BirthCertificate: 'Birth Certificate',
  UndertakingLetter: 'Undertaking Letter',
  LabourContract: 'Labour Contract',
  KYC: 'KYC',
  Census: 'Census',
  FitnessForm: 'Fitness Form',
  Others: 'Others (Relevant Reports)',
  TradeLicense: 'Trade License / Company Name',
  EstablishmentCard: 'Establishment Card',
  DirectorsIDs: 'Directors IDs',
  MOA_AOA_HolderIDs: 'MOA / AOA / Holder IDs Certificate',
  VATCertificate: 'VAT Certificate',
};

export const DOCUMENT_TOOLTIPS: Record<string, string> = {
  ResidenceVisa: 'Upload the applicant valid UAE Visa or Entry Permit, if available.',
  EmiratesId: 'Upload the Emirates ID, or the Emirates ID application if the card is still under process.',
  MerForm: 'Upload MER if any medical condition is declared in MAF by the respective member.',
  LabourContract: 'Required for specific basic policies, such as Orient Insurance PJSC and Watania (EMED policy).',
  UndertakingLetter: 'Upload if Visa and Emirates ID are under process for specific basic policy scenarios.',
  FitnessForm: 'Upload if required based on the applicant age and insurer rules for elderly applicants.',
  Others: 'Upload additional medical documents such as prescriptions, lab tests, or other relevant records.',
};

export const DEFAULT_EXTRACTED_FIELD_LABELS: Record<string, Array<{ key: string; label: string }>> = {
  Passport: [
    { key: 'firstName', label: 'First Name' },
    { key: 'lastName', label: 'Last Name' },
    { key: 'dateOfBirth', label: 'Date of Birth (Age in yrs)' },
    { key: 'gender', label: 'Gender' },
  ],
  ResidenceVisa: [
    { key: 'VisaIDNumber', label: 'Visa File Number' },
    { key: 'fullName', label: 'Full Name' },
    { key: 'dateOfBirth', label: 'Date of Birth (Age in yrs)' },
  ],
  EmiratesId: [
    { key: 'emiratesId', label: 'Emirates ID Number' },
    { key: 'fullName', label: 'Full Name' },
    { key: 'dateOfBirth', label: 'Date of Birth (Age in yrs)' },
  ],
  TradeLicense: [
    { key: 'tradeLicenseNo', label: 'Trade license No.' },
    { key: 'sponsorName', label: 'Sponsor Name' },
  ],
  EstablishmentCard: [
    { key: 'establishmentCardNo', label: 'Establishment Card No.' },
    { key: 'companyName', label: 'Company Name' },
  ],
  VATCertificate: [
    { key: 'vatRegistrationNo', label: 'VAT Registration No.' },
    { key: 'companyName', label: 'Company Name' },
  ],
};

export const OVERRIDE_REASON_OPTIONS: EditableOverrideReasonOption[] = [
  {
    value: 'OCR_ERROR',
    label: 'OCR Error',
    description: 'Text extraction failed or captured incorrect values despite a valid document.',
  },
  {
    value: 'POOR_DOC_QUALITY',
    label: 'Poor Doc Quality',
    description: 'Document readability issues from low light, blur, or low resolution.',
  },
  {
    value: 'PARTIAL_EXTRACTION',
    label: 'Partial Extraction',
    description: 'Only part of the data was extracted due to smudged, damaged, or unclear sections.',
  },
  {
    value: 'FORMAT_DIFFERENCE',
    label: 'Format Difference',
    description: 'Mismatch caused by data format differences.',
  },
  {
    value: 'INCORRECT_DOCUMENT',
    label: 'Incorrect Document',
    description: 'Uploaded document does not correspond to the required document type or field.',
  },
];

export const EXTRACTION_COMMON_KEY_ALIASES: Record<string, string> =
  EXTRACTION_COMMON_KEY_ALIASES_BASE;

export const EXTRACTION_DOC_TYPE_KEY_ALIASES: Record<string, Record<string, string>> =
  EXTRACTION_DOC_TYPE_KEY_ALIASES_BASE;

export const EXTRACTION_HIDDEN_KEYS: string[] = ['DateOfExpiry'];

export const EXTRACTION_FIELD_LABELS: Record<string, string> = {
  PassportNo: 'Passport Number',
  FirstName: 'First Name',
  SecondName: 'Middle Name',
  LastName: 'Last Name',
  VisaIDNumber: 'Visa File Number',
  UIDNo: 'UID Number',
  SponsorName: 'Sponsor Name',
  Profession: 'Occupation',
  DateOfBirth: 'Date of Birth',
  Emirate: 'Emirate',
  Gender: 'Gender',
  EmiratesID: 'Emirates ID Number',
  TradeLicenseNo: 'Trade License No.',
  EstablishmentNo: 'Establishment No.',
  VatCertificateNo: 'VAT Certificate No.',
};
